import type { GA4Item, GA4PurchaseParams } from "./types";

function pushGA4Ecommerce(
  event: string,
  params: Record<string, unknown>,
): void {
  if (typeof window === "undefined" || !window.dataLayer) return;
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ecommerce: params });
}

function pushGA4Event(event: string, params?: Record<string, unknown>): void {
  if (typeof window === "undefined" || !window.dataLayer) return;
  window.dataLayer.push({ event, ...params });
}

export function trackGA4ViewItemList(
  items: GA4Item[],
  listId?: string,
  listName?: string,
): void {
  pushGA4Ecommerce("view_item_list", {
    item_list_id: listId,
    item_list_name: listName,
    items,
  });
}

export function trackGA4SelectItem(
  item: GA4Item,
  listId?: string,
  listName?: string,
): void {
  pushGA4Ecommerce("select_item", {
    item_list_id: listId,
    item_list_name: listName,
    items: [item],
  });
}

export function trackGA4ViewItem(
  item: GA4Item,
  value: number,
  currency = "USD",
): void {
  pushGA4Ecommerce("view_item", { currency, value, items: [item] });
}

export function trackGA4AddToCart(
  item: GA4Item,
  value: number,
  currency = "USD",
): void {
  pushGA4Ecommerce("add_to_cart", { currency, value, items: [item] });
}

export function trackGA4RemoveFromCart(
  item: GA4Item,
  value: number,
  currency = "USD",
): void {
  pushGA4Ecommerce("remove_from_cart", { currency, value, items: [item] });
}

export function trackGA4ViewCart(
  items: GA4Item[],
  value: number,
  currency = "USD",
): void {
  pushGA4Ecommerce("view_cart", { currency, value, items });
}

export function trackGA4BeginCheckout(
  items: GA4Item[],
  value: number,
  coupon?: string,
  currency = "USD",
): void {
  pushGA4Ecommerce("begin_checkout", {
    currency,
    value,
    ...(coupon && { coupon }),
    items,
  });
}

export function trackGA4AddShippingInfo(
  items: GA4Item[],
  value: number,
  shippingTier?: string,
  currency = "USD",
): void {
  pushGA4Ecommerce("add_shipping_info", {
    currency,
    value,
    ...(shippingTier && { shipping_tier: shippingTier }),
    items,
  });
}

export function trackGA4AddPaymentInfo(
  items: GA4Item[],
  value: number,
  paymentType?: string,
  currency = "USD",
): void {
  pushGA4Ecommerce("add_payment_info", {
    currency,
    value,
    ...(paymentType && { payment_type: paymentType }),
    items,
  });
}

export function trackGA4Purchase(params: GA4PurchaseParams): void {
  pushGA4Ecommerce("purchase", {
    transaction_id: params.transaction_id,
    value: params.value,
    currency: params.currency,
    ...(params.tax != null && { tax: params.tax }),
    ...(params.shipping != null && { shipping: params.shipping }),
    items: params.items,
  });
}

export function trackGA4Search(searchTerm: string): void {
  pushGA4Event("search", { search_term: searchTerm });
}

export function trackGA4SignUp(method: string): void {
  pushGA4Event("sign_up", { method });
}

export function trackGA4Login(method: string): void {
  pushGA4Event("login", { method });
}
